import React from "react";
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Checkbox,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {useTranslation} from "react-i18next";
import type {WorkEvidenceRecord} from "../WorkEvidenceApprovalTableComponent";
import StatusChip from "../StatusChip";
import {getAvatarColors, getInitials} from "../../../utils/avatarUtils";

// ─── Constants ────────────────────────────────────────────────────────────────

const HOUR_FIELDS: { key: keyof WorkEvidenceRecord; labelKey: string }[] = [
    {key: "regularHours", labelKey: "workEvidence.table.header.regularHours"},
    {key: "overTimeHours", labelKey: "workEvidence.table.header.overtimeHours"},
    {key: "nightHours", labelKey: "workEvidence.table.header.nightHours"},
    {key: "weekendHours", labelKey: "workEvidence.table.header.weekendHours"},
    {key: "holidayHours", labelKey: "workEvidence.table.header.holidayHours"},
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface WorkEvidenceApprovalTableComponentMobileProps {
    filteredData: WorkEvidenceRecord[];
    currentPage: number;
    itemsPerPage: number;
    canApprove: boolean;
    isPeriodLocked: boolean;
    selected: string[];
    approvingId: string | null;
    rejectingId: string | null;
    isSelected: (id: string) => boolean;
    handleRowCheckboxClick: (event: React.MouseEvent, id: string) => void;
    handleApprove: (record: WorkEvidenceRecord) => void;
    handleReject: (record: WorkEvidenceRecord) => void;
    isLoading?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const WorkEvidenceApprovalTableComponentMobile: React.FC<WorkEvidenceApprovalTableComponentMobileProps> = ({
                                                                                                               filteredData,
                                                                                                               currentPage,
                                                                                                               itemsPerPage,
                                                                                                               canApprove,
                                                                                                               isPeriodLocked,
                                                                                                               selected,
                                                                                                               approvingId,
                                                                                                               rejectingId,
                                                                                                               isSelected,
                                                                                                               handleRowCheckboxClick,
                                                                                                               handleApprove,
                                                                                                               handleReject,
                                                                                                               isLoading = false,
                                                                                                           }) => {
    const {t} = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [menuRowId, setMenuRowId] = React.useState<string | null>(null);
    const menuOpen = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuRowId(rowId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRowId(null);
    };

    const handleMenuAction = (action: () => void) => {
        action();
        handleMenuClose();
    };

    const paginatedData = React.useMemo(
        () => filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
        [filteredData, currentPage, itemsPerPage],
    );

    if (isLoading) {
        return (
            <Stack spacing={2}>
                {Array.from({length: 4}).map((_, i) => (
                    <Card key={`skeleton-${i}`} variant="outlined" sx={{backgroundColor: "#fff"}}>
                        <CardContent>
                            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1}}>
                                <Skeleton variant="rectangular" width={24} height={24}/>
                                <Skeleton variant="rounded" width={80} height={22}/>
                            </Box>
                            <Skeleton variant="text" width="55%" height={28} sx={{mb: 1.5}}/>
                            {Array.from({length: 5}).map((__, j) => (
                                <Box key={j} sx={{display: "flex", justifyContent: "space-between", mb: 0.75}}>
                                    <Skeleton variant="text" width="40%"/>
                                    <Skeleton variant="text" width="20%"/>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        );
    }

    if (paginatedData.length === 0) {
        return (
            <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 1}}>
                <ManageHistoryIcon sx={{fontSize: 48, color: "text.disabled"}}/>
                <Typography variant="body1" color="text.secondary">{t('workEvidence.approval.empty.title')}</Typography>
                <Typography variant="body2" color="text.disabled" sx={{textAlign: "center"}}>
                    {t('workEvidence.approval.empty.selectPeriodBranch')}
                </Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            {paginatedData.map((record) => {
                const rowSelected = isSelected(record.employeeId);
                const isApproved = record.status?.toUpperCase().includes("APPROVED");
                const isRejected = record.status?.toUpperCase().includes("REJECTED");
                const actionsDisabled = !canApprove || isPeriodLocked;
                const avatarColors = getAvatarColors(`${record.firstName}${record.lastName}${record.employeeId}`);

                return (
                    <Card
                        key={record.employeeId}
                        variant="outlined"
                        sx={{
                            backgroundColor: rowSelected ? "action.selected" : "#fff",
                            transition: "box-shadow 0.2s ease",
                            "&:hover": {boxShadow: 2},
                        }}
                    >
                        <CardContent sx={{pb: "12px !important"}}>
                            {/* Header: checkbox + status chip + 3-dot menu */}
                            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                    <Checkbox
                                        color="primary"
                                        checked={rowSelected}
                                        onClick={(e) => handleRowCheckboxClick(e, record.employeeId)}
                                        aria-label={t('workEvidence.approval.aria.selectRecord', {firstName: record.firstName, lastName: record.lastName})}
                                        sx={{p: 0.5}}
                                    />
                                    <StatusChip status={record.status} size="small"/>
                                </Box>

                                {/* 3-dot action menu */}
                                <Box onClick={(e) => e.stopPropagation()}>
                                    <IconButton
                                        size="small"
                                        aria-label={t('workEvidence.approval.aria.actionsForRecord', {firstName: record.firstName, lastName: record.lastName})}
                                        disabled={actionsDisabled}
                                        onClick={(e) => handleMenuOpen(e, record.employeeId)}
                                        sx={{bgcolor: "action.hover", "&:hover": {bgcolor: "action.selected"}}}
                                    >
                                        <MoreVertIcon fontSize="small"/>
                                    </IconButton>
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={menuOpen && menuRowId === record.employeeId}
                                        onClose={handleMenuClose}
                                        slotProps={{paper: {style: {maxHeight: 48 * 4.5, width: "18ch"}}}}
                                    >
                                        <MenuItem
                                            onClick={() => handleMenuAction(() => handleApprove(record))}
                                            disabled={Boolean(isApproved) || approvingId === record.employeeId}
                                        >
                                            <ListItemIcon>
                                                <CheckCircleOutlineIcon fontSize="small" color="success"/>
                                            </ListItemIcon>
                                            <ListItemText>
                                                {approvingId === record.employeeId ? t('workEvidence.approval.approving') : t('common.actions.approve')}
                                            </ListItemText>
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => handleMenuAction(() => handleReject(record))}
                                            disabled={Boolean(isRejected) || rejectingId === record.employeeId}
                                        >
                                            <ListItemIcon>
                                                <CancelOutlinedIcon fontSize="small" color="error"/>
                                            </ListItemIcon>
                                            <ListItemText>
                                                {rejectingId === record.employeeId ? t('workEvidence.approval.rejecting') : t('common.actions.reject')}
                                            </ListItemText>
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            </Box>

                            {/* Name / branch */}
                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 1.5}}>
                                <Avatar sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: avatarColors.bg,
                                    color: avatarColors.color,
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    flexShrink: 0,
                                }}>
                                    {getInitials(record.firstName, record.lastName)}
                                </Avatar>
                                <Box sx={{minWidth: 0}}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{wordBreak: "break-word", textAlign: "left"}}
                                    >
                                        {record.firstName} {record.lastName}
                                    </Typography>
                                    {record.branchName && (
                                        <Typography variant="caption" color="text.secondary" sx={{display: "block", textAlign: "left"}}>
                                            {record.branchName}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Divider sx={{mb: 1.5}}/>

                            {/* Hours fields */}
                            <Box sx={{display: "flex", flexDirection: "column", gap: 0.75}}>
                                {HOUR_FIELDS.map(({key, labelKey}) => (
                                    <Box key={key}
                                         sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                        <Typography variant="caption" color="text.secondary">{t(labelKey)}</Typography>
                                        <Typography variant="caption" fontWeight={500}>
                                            {(record[key] as number | null) ?? "-"}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            {/* Comment */}
                            {record.comment && (
                                <>
                                    <Divider sx={{my: 1.5}}/>
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start"
                                    }}>
                                        <Typography variant="caption" color="text.secondary">{t('workEvidence.mobile.comment')}</Typography>
                                        <Typography
                                            variant="caption"
                                            fontWeight={500}
                                            sx={{textAlign: "right", maxWidth: "65%", wordBreak: "break-word"}}
                                        >
                                            {record.comment}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default WorkEvidenceApprovalTableComponentMobile;
